<template>
  <div :data-testid="dataTestid" aria-hidden="true">
    <VChart :option="option" :theme="OMR_ECHARTS_THEME" autoresize class="h-full w-full" :data-testid="`${dataTestid}-chart`" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { OMR_ECHARTS_THEME } from '@/composables/useOmrEchartsTheme'
import { VChart, type OmrEChartsOption } from '@/config/echarts'

const SCALE_LIMIT = 100
const BAR_RADIUS = 4

const props = withDefaults(
  defineProps<{
    value: number
    positiveColor: string
    negativeColor: string
    trackColor: string
    zeroColor: string
    marker?: number | null
    markerColor?: string
    markerLabel?: string
    dataTestid?: string
  }>(),
  { marker: null, markerColor: undefined, markerLabel: undefined, dataTestid: 'insights-diverging-bar' },
)

const clampedValue = computed(() => Math.min(SCALE_LIMIT, Math.max(-SCALE_LIMIT, props.value)))

const option = computed<OmrEChartsOption>(() => ({
  grid: { top: 0, left: 0, right: 0, bottom: 0 },
  ...(props.markerLabel && { tooltip: { trigger: 'item', position: 'top', appendToBody: true } }),
  xAxis: { type: 'value', min: -SCALE_LIMIT, max: SCALE_LIMIT, show: false },
  yAxis: { type: 'category', data: [''], show: false },
  series: [
    {
      type: 'bar',
      data: [clampedValue.value],
      barWidth: '100%',
      silent: true,
      clip: false,
      itemStyle: { color: clampedValue.value >= 0 ? props.positiveColor : props.negativeColor, borderRadius: BAR_RADIUS },
      showBackground: true,
      backgroundStyle: { color: props.trackColor, borderRadius: BAR_RADIUS },
      markLine: {
        silent: true,
        symbol: 'none',
        label: { show: false },
        lineStyle: { color: props.zeroColor, width: 1, type: 'solid' },
        data: [{ xAxis: 0 }],
      },
    },
    ...(props.marker != null
      ? [
          {
            type: 'bar' as const,
            data: [null],
            silent: !props.markerLabel,
            emphasis: { disabled: true },
            tooltip: { show: false },
            markLine: {
              silent: !props.markerLabel,
              symbol: 'none',
              label: { show: false },
              lineStyle: { color: props.markerColor, width: 2, type: 'solid' as const },
              emphasis: { lineStyle: { width: 4 } },
              tooltip: { formatter: () => props.markerLabel ?? '' },
              data: [{ xAxis: props.marker }],
            },
          },
        ]
      : []),
  ],
}))
</script>
